import { Box, Button, CircularProgress, Stack } from '@mui/material';
import { Close as CloseIcon, NavigateBefore as NavigateBeforeIcon } from '@mui/icons-material';
import API from '../../API';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { sharedClasses } from '../../../Components/CustomUIElements/sharedClasses';
import { IError } from '../../../SmartForms/types';
import { IEvent, ISMSTemplate, IAddToEventModalProps, IRecruiter } from '../../types';
import { classes } from '../styles';
import { usePlaceholders } from '../../../hooks';
import { useEmailTemplatesOptions } from '../../../hooks';
import { scrollToElement } from '../../../utils/scroll-to-element';
import formatToUserTimeZone from '../../../utils/formatToUserTimeZone';
import InviteToTimeslotStep1 from './InviteToTimeslotStep1';
import InviteToTimeslotStep2 from './InviteToTimeslotStep2';
import { IEventType } from '../../../../ScoutCalendar/types';
import NoTimeslotsDialog from '../Modals/NoTimeslotsDialog';
import CreateEventButton from '../../../Components/Buttons/CreateEventButton';
import StyledModal from '../../../Components/GenericModal/StyledModal';

interface IJob {
  id: number;
  reference: string;
  title: string;
}

export default function NewInviteToTimeslot({
  jobId,
  setSnackbar,
  modalsOpen,
  setModalsOpen,
  globalSelected,
  handleDeselectCandidate,
  filters,
  smsTemplatePermissions,
  calendarDispatch,
  ScoutCalendarState
}: IAddToEventModalProps) {
  const [isSending, setIsSending] = useState(false);
  const [isLoading, setIsLoading] = useState({
    eventTypes: false,
    events: false
  });
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [body, setBody] = useState('');
  const [subject, setSubject] = useState('');
  const [sendableBody, setSendableBody] = useState('');
  const [selectedRecruiter, setSelectedRecruiter] = useState<IRecruiter | null>(null);
  const [selectedJob, setSelectedJob] = useState<string | null>(null);
  const [eventTypes, setEventTypes] = useState<IEventType[]>([]);
  const [selectedEventType, setSelectedEventType] = useState<IEventType | null>(null);
  const [excludeApplicants, setExcludeApplicants] = useState(false);
  const [allEvents, setAllEvents] = useState<IEvent[]>([]);
  const [events, setEvents] = useState<IEvent[]>([]);
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<string>(
    formatToUserTimeZone(new Date(), 'ymd')
  );
  const [step, setStep] = useState<number>(1);
  const [timeslotHovered, setTimeslotHovered] = useState<number | null>(null);
  const recruiterRef = useRef<HTMLDivElement>(null);
  const eventTypeRef = useRef<HTMLDivElement>(null);
  const templateRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const [notification, setNotification] = useState<string>('email');
  const [smsMessage, setSmsMessage] = useState<string>('');
  const [smsTemplate, setSmsTemplate] = useState<ISMSTemplate | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [filterType, setFilterType] = useState<'owner' | 'job' | 'recruiter'>('owner');
  const [spots, setSpots] = useState<number>(0);
  const [showNoTimeslotsModal, setShowNoTimeslotsModal] = useState(false);
  const [noTimeslots, setNoTimeslots] = useState<boolean>(false);
  const [notEnoughSpots, setNotEnoughSpots] = useState<boolean>(false);

  const buttonName = step === 1 ? 'Next' : 'Send';

  const { data: emailTemplates = [], isLoading: loadingEmailTemplates } = useEmailTemplatesOptions({
    onError: (message: string) => setSnackbar({ message, state: 'error' })
  });

  const { data: placeholders } = usePlaceholders({
    type: 'interview',
    onError: (message: string) => setSnackbar({ message, state: 'error' as const })
  });

  const { data: recruiters, isLoading: loadingRecruiters } = useQuery({
    queryKey: ['recruiters'],
    queryFn: async () => {
      const { users } = await API.getRecruiters();
      return users;
    },
    onError: (error: IError) =>
      setSnackbar({ message: error.errors.join(', '), state: 'error' as const })
  });

  const { data: jobs, isLoading: loadingJobs } = useQuery({
    queryKey: ['jobs'],
    queryFn: async () => {
      const { jobs } = await API.getEventJobs();
      return jobs;
    }
  });

  const jobOptions = jobs?.map((job: IJob) => `${job.id} - ${job.reference} - ${job.title}`);

  const { mutate: sendSms, isLoading: sendingSms } = useMutation({
    mutationFn: async () => {
      if (!selectedEventType) return;

      const res = await API.sendInviteSms(
        jobId,
        selectedEventType.id,
        globalSelected.map((a) => a.id),
        smsMessage,
        smsTemplate?.id
      );
      if (res.success !== 0) {
        setSnackbar({
          message: 'Invitation to the event has been sent successfully',
          state: 'success'
        });
        setModalsOpen({ inviteToTimeslot: false });
      } else {
        setSnackbar({ message: 'There is an error in sending SMS invitation', state: 'error' });
      }
      return res;
    }
  });

  const getEventTypes = useCallback(async () => {
    setIsLoading((prev) => ({ ...prev, eventTypes: true }));
    try {
      const response = await fetch(`/api/v4/calendar/event_types`, { method: 'GET' });
      const data = await response.json();
      setEventTypes(data.event_types);
    } catch (error) {
      setSnackbar({
        message: 'Failed to load event types',
        state: 'error'
      });
    } finally {
      setIsLoading((prev) => ({ ...prev, eventTypes: false }));
    }
  }, [setSnackbar]);

  const handleSelectTemplate = async (template: string) => {
    if (!template) {
      setSelectedTemplate(null);
      setBody('');
      setSendableBody('');
      return;
    }
    setSelectedTemplate(template);
    const templateId = template.split(' -')[0];
    try {
      const response = await fetch(`/api/v4/calendar/email_templates/${templateId}`, {
        method: 'GET'
      });
      const data = await response.json();
      setSubject(data.subject);
      setBody(data.body);
      setSendableBody(data.body);
      if (response.status !== 200) {
        throw new Error(data);
      }
    } catch (error) {
      setSnackbar({
        message: 'Error loading template',
        state: 'error'
      });
      setBody('');
    }
  };

  const getTimeslots = useCallback(
    async (eventId?: number) => {
      setIsLoading((prev) => ({ ...prev, events: true }));
      const date = new Date().toISOString();
      const queryString = `/api/v4/calendar/events?${
        eventId ? 'event_type_id=' + eventId : ''
      }&date=${date}&override_limit=true`;
      try {
        const response = await fetch(queryString, { method: 'GET' });
        const data = await response.json();
        setAllEvents(data.events || []);
      } catch (error) {
        setSnackbar({
          message: 'Failed to load timeslots',
          state: 'error'
        });
      } finally {
        setIsLoading((prev) => ({ ...prev, events: false }));
      }
    },
    [setSnackbar]
  );

  const handleClose = () => setModalsOpen({ inviteToTimeslot: false });

  const validateInput = () => {
    const fieldsErrors: Record<string, string> = {};
    if (step === 1) {
      !filterType && (fieldsErrors.recruiter = 'Must select a recruiter');
      !selectedEventType && (fieldsErrors.eventType = 'Must select an event type');
    } else {
      if (notification === 'email') {
        !selectedTemplate && (fieldsErrors.template = 'Must select a template');
        !sendableBody.trim() && (fieldsErrors.body = 'Must add email body');
      } else {
        !smsMessage.trim() && (fieldsErrors.sms = 'Must add SMS content');
      }
    }
    setErrors(fieldsErrors);
    if (fieldsErrors.recruiter) {
      scrollToElement(recruiterRef);
    } else if (fieldsErrors.eventType) {
      scrollToElement(eventTypeRef);
    } else if (fieldsErrors.template) {
      scrollToElement(templateRef);
    } else if (fieldsErrors.body) {
      scrollToElement(bodyRef);
    }
    return !Object.keys(fieldsErrors).length;
  };

  const handleSubmit = async () => {
    if (step === 1) {
      if (events.length === 0) {
        setNoTimeslots(true);
        setNotEnoughSpots(false);
        setShowNoTimeslotsModal(true);
        return;
      } else if (events.length < globalSelected.length) {
        setNotEnoughSpots(true);
        setNoTimeslots(false);
        setShowNoTimeslotsModal(true);
        return;
      }
      validateInput() && setStep(step + 1);
    } else {
      notification === 'email'
        ? validateInput() && handleSubmitEmail()
        : validateInput() && sendSms();
    }
  };

  const handleSubmitEmail = async () => {
    setIsSending(true);
    const templateId = selectedTemplate?.split(' -')[0];
    try {
      const response = await fetch('/api/v4/calendar/invitations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: selectedRecruiter?.id,
          event_type_id: selectedEventType?.id,
          job_id: jobId,
          invitation: {
            application_ids: globalSelected.map((a) => a.id),
            subject: subject,
            body: sendableBody,
            ...(selectedTemplate ? { email_template_id: templateId } : {}),
            only_new: Number(excludeApplicants)
          }
        })
      });
      if (response.ok) {
        setSnackbar({
          message: 'Timeslot invitations sent successfully',
          state: 'success'
        });
        handleClose();
      } else {
        setSnackbar({
          message: 'Failed to send timeslot invitations, please try again',
          state: 'error'
        });
      }
    } catch (error) {
      setSnackbar({
        message: 'Failed to send timeslot invitations, please try again',
        state: 'error'
      });
    } finally {
      setIsSending(false);
    }
  };

  useEffect(() => {
    if (modalsOpen.inviteToTimeslot) {
      getTimeslots();
      getEventTypes();
    }
  }, [getEventTypes, getTimeslots, modalsOpen.inviteToTimeslot, ScoutCalendarState]);

  useEffect(() => {
    let filtered = allEvents;
    setSelectedDay(null);

    if (selectedEventType) {
      filtered = filtered.filter((event) => event.event_type?.id === selectedEventType.id);
    }

    if (filterType === 'recruiter' && selectedRecruiter) {
      filtered = filtered.filter((event) =>
        event.attendees?.some(
          (attendee) => attendee.type === 'User' && attendee.id === selectedRecruiter.id
        )
      );
    } else if (filterType === 'job' && selectedJob) {
      const jobId = Number(selectedJob.split(' -')[0]);
      filtered = filtered.filter((event) => {
        if (Array.isArray(event.jobs)) {
          return event.jobs.some((job) => job.id === jobId);
        }
        if (event.jobs && typeof event.jobs === 'object') {
          return event.jobs.id === jobId;
        }
        return false;
      });
    } else if (filterType === 'owner' && selectedRecruiter) {
      filtered = filtered.filter((event) => event.owner_id === selectedRecruiter.id);
    }

    setEvents(filtered);
    setSpots(filtered.reduce((sum, event) => sum + (event.available_spots || 0), 0));
  }, [selectedEventType, filterType, selectedRecruiter, selectedJob, allEvents]);

  return (
    <>
      <StyledModal
        isOpen={modalsOpen.inviteToTimeslot}
        label="Invite to timeslot modal"
        handleClose={handleClose}
        styleOverrides={{ width: '900px' }}
        disableEnforceFocus
      >
        <Box sx={classes.actionsModalReducedPadding}>
          <CloseIcon onClick={handleClose} sx={classes.closeIcon} />
          <Box
            id="invite-to-event-title"
            sx={{
              ...classes.actionModalHeader,
              paddingBottom: 3,
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 2
            }}
          >
            <Stack>Invite to Event</Stack>
            {step === 1 && (
              <CreateEventButton
                dispatch={calendarDispatch}
                sx={{ ...classes.createEventButton, margin: 0 }}
                id="create-event-button"
              />
            )}
          </Box>
          <Box
            sx={{ padding: '0px 8px', flexGrow: 1, overflow: 'auto' }}
            id="invite-to-event-modal-content"
            aria-label="Invite to event modal content"
          >
            <Box role="form" id="invite-to-event-form" aria-label="Invite to event form">
              {step === 1 && (
                <InviteToTimeslotStep1
                  eventTypes={eventTypes}
                  selectedEventType={selectedEventType}
                  setSelectedEventType={setSelectedEventType}
                  errors={errors}
                  setErrors={setErrors}
                  filterType={filterType}
                  setFilterType={setFilterType}
                  selectedRecruiter={selectedRecruiter}
                  setSelectedRecruiter={setSelectedRecruiter}
                  selectedJob={selectedJob}
                  setSelectedJob={setSelectedJob}
                  recruiters={recruiters}
                  loadingRecruiters={loadingRecruiters}
                  jobOptions={jobOptions}
                  loadingJobs={loadingJobs}
                  allEvents={allEvents}
                  events={events}
                  isLoading={isLoading}
                  spots={spots}
                  globalSelected={globalSelected}
                  handleDeselectCandidate={handleDeselectCandidate}
                  filters={filters}
                  selectedDay={selectedDay}
                  setSelectedDay={setSelectedDay}
                  selectedMonth={selectedMonth}
                  setSelectedMonth={setSelectedMonth}
                  timeslotHovered={timeslotHovered}
                  setTimeslotHovered={setTimeslotHovered}
                  eventTypeRef={eventTypeRef}
                  isApplicationPage={false}
                />
              )}
              {step === 2 && (
                <InviteToTimeslotStep2
                  notification={notification}
                  setNotification={setNotification}
                  selectedTemplate={selectedTemplate}
                  setSelectedTemplate={setSelectedTemplate}
                  emailTemplates={emailTemplates}
                  loadingEmailTemplates={loadingEmailTemplates}
                  handleSelectTemplate={handleSelectTemplate}
                  errors={errors}
                  setErrors={setErrors}
                  body={body}
                  setBody={setBody}
                  sendableBody={sendableBody}
                  setSendableBody={setSendableBody}
                  smsMessage={smsMessage}
                  setSmsMessage={setSmsMessage}
                  smsTemplate={smsTemplate}
                  setSmsTemplate={setSmsTemplate}
                  excludeApplicants={excludeApplicants}
                  setExcludeApplicants={setExcludeApplicants}
                  placeholders={placeholders}
                  templateRef={templateRef}
                  bodyRef={bodyRef}
                  smsTemplatePermissions={smsTemplatePermissions}
                  setSnackbar={setSnackbar}
                />
              )}
            </Box>
          </Box>
          <Box sx={{ ...classes.modalFooter, columnGap: 1, paddingTop: 2 }}>
            <Button
              id="close-invite-to-event-button"
              sx={classes.modalCancelButton}
              onClick={handleClose}
            >
              Cancel
            </Button>
            {step !== 1 && (
              <Button
                onClick={() => setStep(1)}
                sx={{ ...sharedClasses.genericButtonSecondary, minWidth: 'unset', padding: 1 }}
              >
                <NavigateBeforeIcon />
              </Button>
            )}
            <Button
              id="send-invite-to-event-button"
              type="submit"
              sx={{ ...classes.modalSendButton, width: '71px', height: '41px' }}
              disabled={
                loadingJobs || loadingRecruiters || isLoading.eventTypes || isLoading.events
              }
              onClick={handleSubmit}
            >
              {isSending || sendingSms ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                buttonName
              )}
            </Button>
          </Box>
        </Box>
      </StyledModal>
      <NoTimeslotsDialog
        open={showNoTimeslotsModal}
        noTimeslots={noTimeslots}
        notEnoughSpots={notEnoughSpots}
        onClose={() => setShowNoTimeslotsModal(false)}
        onContinue={() => {
          setNoTimeslots(false);
          setNotEnoughSpots(false);
          setShowNoTimeslotsModal(false);
          setStep(2);
        }}
      />
    </>
  );
}
